/* eslint-disable lit-a11y/click-events-have-key-events */
/* eslint-disable class-methods-use-this */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { html, css } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { BaseElement, customElement } from '../../base-element';
import { Translate } from '../../base-element/mixins/translation-mixin';

export interface SimpleFileuploadProps {
  readonly?: boolean;
  previewLabel?: string;
  previewCta?: string;
  accept?: string;
  allowMultiple?: boolean;
  onChange?: any;
  name?: string;
}

@customElement('ps-simple-fileupload')
export class SimpleFileuploadWC extends Translate(BaseElement) {
  @property({ type: String }) accept: SimpleFileuploadProps['accept'] = '';

  @property() name?: SimpleFileuploadProps['name'];

  @property({ reflect: true, attribute: 'preview-label' })
  previewLabel: SimpleFileuploadProps['previewLabel'] =
    'Drag & drop files here';

  @property({ reflect: true, attribute: 'preview-cta' })
  previewCta: SimpleFileuploadProps['previewCta'] = 'Select a file';

  @property({ type: Boolean }) readonly?: SimpleFileuploadProps['readonly'];

  @property({ type: Boolean, attribute: 'allow-multiple' })
  allowMultiple?: SimpleFileuploadProps['allowMultiple'];

  @property() onChange: SimpleFileuploadProps['onChange'];

  static styles = css`
    .upload-container {
      border: 2px dashed #e5e7eb;
      border-radius: 12px;
      padding: 24px;
      background-color: #ffffff;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 16px;
      min-height: 80px;
    }

    .upload-container:not(.readonly) {
      cursor: pointer;
    }

    .upload-container.readonly {
      cursor: not-allowed;
      opacity: 0.6;
    }

    .upload-container:not(.readonly):hover {
      border-color: #8a6ff7;
      background-color: #f8f7ff;
    }

    .icon-wrapper {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background-color: #e0d9f7;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .icon-wrapper ps-icon {
      color: #8a6ff7;
    }

    .text-block {
      flex: 1;
    }

    .label-text {
      font-size: 14px;
      color: #374151;
      margin-bottom: 4px;
      font-weight: 400;
    }

    .cta-text {
      font-size: 14px;
      color: #8a6ff7;
      font-weight: 500;
      cursor: pointer;
    }

    input[type='file'] {
      display: none;
    }
  `;

  handleClick = () => {
    if (!this.readonly) {
      const fileInput = this.shadowRoot?.querySelector(
        '#hidden-file-input'
      ) as HTMLInputElement;
      fileInput.click();
    }
  };

  handleFileChange = (event: Event) => {
    const { files } = event.target as HTMLInputElement;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      this.onChange({ files: fileArray });
    }
  };

  handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  handleDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.readonly) return;

    const { dataTransfer } = event;

    if (!dataTransfer) return;
    const { files } = dataTransfer;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      this.onChange({ files: fileArray });
    }
  };

  render() {
    const containerStyle = styleMap({
      opacity: this.readonly ? '0.6' : '1',
    });

    return html`
      <div
        class="upload-container ${this.readonly ? 'readonly' : ''}"
        style=${containerStyle}
        @click=${this.handleClick}
        @dragover=${this.handleDragOver}
        @drop=${this.handleDrop}
      >
        <div class="icon-wrapper">
          <ps-icon name="arrow-up" size="small"></ps-icon>
        </div>

        <div class="text-block">
          <div class="label-text">${this.previewLabel}</div>
          <div class="cta-text">${this.previewCta}</div>
        </div>

        <input
          id="hidden-file-input"
          type="file"
          .accept=${this.accept || ''}
          .multiple=${this.allowMultiple || true}
          .disabled=${this.readonly || false}
          @change=${this.handleFileChange}
        />
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-simple-fileupload': SimpleFileuploadWC;
  }
  enum PSElementTagNameMap {
    'ps-simple-fileupload' = 'ps-simple-fileupload',
  }
}
